// New Season — switch a flagged user's reads to V2.
//
// The rule: V1 reads only the old fields table, V2 reads only the new tables
// (seasons / blocks / field_groups); both write to both. The API serves every
// V1 read under `v2/` too (GET /v2/<v1 path>), with the same response shape,
// read from the new tables. So for a user with the `new_season` remote
// configuration, this prefixes every GET with `v2/`. Writes (POST/PUT/DELETE)
// are left on their V1 paths, which already write both tables.
//
// Users without the flag are untouched: the interceptor returns the request
// as is.
//
// Timing: the app requests the distribution tree before the remote
// configuration has loaded, and waiting for it would slow every user's start.
// So the last known value is remembered in localStorage and used from the
// first request. When the configuration arrives and disagrees, the stored
// value is updated and the page reloads once so every screen reads from one
// source. If storage is unavailable, later requests still follow the flag but
// the page does not reload (a reload could not remember the answer and would
// loop).

// The instance the shared managers call -- not this app's own axios copy,
// which may be a different module (see sciroot-clients-shared httpClient).
const sharedHttpClient = require('@managers/httpClient');
const {
  get_configuration_config,
  FeatureFlagDictionary,
} = require('remoteConfigurationsManager');

const STORAGE_KEY = 'new_season_v2_reads';
const V2_PREFIX = 'v2/';

const readStoredFlag = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (e) {
    console.log('new_season: localStorage unavailable', e);
    return false;
  }
};

const storeFlag = (enabled) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    return true;
  } catch (e) {
    console.log('new_season: could not store the V2 read flag', e);
    return false;
  }
};

// The flag is per user (users_email), resolved server-side into
// default_value; farming-unit / distribution overrides do not apply to a
// whole-app switch.
const flagFromConfiguration = (configurationData) =>
  Boolean(
    get_configuration_config(
      configurationData,
      FeatureFlagDictionary.NEW_SEASON,
      undefined,
      undefined,
    ),
  );

const isRelative = (url) =>
  typeof url === 'string' && !/^[a-z]+:\/\//i.test(url);

// A request the API mirrors under v2/: a relative GET not already on v2.
const isMirroredRead = (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get' || !isRelative(config.url)) {
    return false;
  }
  const path = config.url.replace(/^\/+/, '');
  return !path.startsWith(V2_PREFIX);
};

const toV2 = (config) => ({
  ...config,
  url: V2_PREFIX + config.url.replace(/^\/+/, ''),
});

const installV2ReadSwitch = (
  store,
  {
    reload = () => window.location.reload(),
    httpClient = sharedHttpClient,
  } = {},
) => {
  let enabled = readStoredFlag();

  const interceptorId = httpClient.interceptors.request.use((config) =>
    enabled && isMirroredRead(config) ? toV2(config) : config,
  );

  let lastConfiguration = null;
  const unsubscribe = store.subscribe(() => {
    const configurationData = store.getState().configuration_data;
    if (
      !configurationData?.configuration_data ||
      configurationData === lastConfiguration
    ) {
      return;
    }
    lastConfiguration = configurationData;
    const next = flagFromConfiguration(configurationData);
    if (next === enabled) {
      return;
    }
    enabled = next;
    if (storeFlag(next)) {
      reload();
    }
  });

  return () => {
    httpClient.interceptors.request.eject(interceptorId);
    unsubscribe();
  };
};

module.exports = {
  installV2ReadSwitch,
  isMirroredRead,
  toV2,
  flagFromConfiguration,
  STORAGE_KEY,
};
